import { ParlayLeg } from '../services/api';

/** Stable identity for a leg (used to de-duplicate and to mark cells already in the slip). */
export const legKey = (leg: ParlayLeg): string =>
  `${leg.kind}|${leg.driver}|${leg.n ?? ''}|${(leg.others ?? []).join(',')}`;

const positionLabel = (n: number): string => (n === 1 ? 'win' : `top ${n}`);

/** Human-readable leg, e.g. "Larson top 10", "Hamlin not top 3", "Bell ahead of Byron, Reddick". */
export const legLabel = (leg: ParlayLeg): string => {
  switch (leg.kind) {
    case 'top_n':
      return `${leg.driver} ${positionLabel(leg.n ?? 1)}`;
    case 'not_top_n':
      return leg.n === 1 ? `${leg.driver} doesn't win` : `${leg.driver} not top ${leg.n}`;
    case 'ahead_of':
      return `${leg.driver} ahead of ${(leg.others ?? []).join(', ')}`;
    default:
      return leg.driver;
  }
};

/** Parse American odds ("+450", "450", "-150"); returns null if it isn't valid. */
export const parseAmericanOdds = (text: string): number | null => {
  const t = text.trim().replace(/\s/g, '');
  if (!/^[+-]?\d+(\.\d+)?$/.test(t)) return null;
  const v = Number(t);
  if (!Number.isFinite(v) || Math.abs(v) < 100) return null;
  return v;
};

/** Implied probability of American odds (no vig removed). */
export const impliedProb = (odds: number): number => (odds > 0 ? 100 / (odds + 100) : -odds / (-odds + 100));

/** Profit on a winning $100 stake at these American odds. */
export const profitPer100 = (odds: number): number => (odds > 0 ? odds : (100 * 100) / -odds);

/** Expected profit per $100 staked, given the true probability of winning. */
export const expectedValuePer100 = (prob: number, odds: number): number =>
  prob * profitPer100(odds) - (1 - prob) * 100;

const SUFFIXES = new Set(['jr', 'jr.', 'sr', 'sr.', 'ii', 'iii', 'iv']);

/** Last name for compact labels ("Ricky Stenhouse Jr" -> "Stenhouse"). */
export const lastName = (driver: string): string => {
  const parts = driver.trim().split(/\s+/);
  while (parts.length > 1 && SUFFIXES.has(parts[parts.length - 1].toLowerCase())) parts.pop();
  return parts[parts.length - 1] ?? driver;
};

/** Compact leg label using last names, for narrow tables ("Larson ahead of Hamlin"). */
export const shortLegLabel = (leg: ParlayLeg): string =>
  legLabel({ ...leg, driver: lastName(leg.driver), others: leg.others?.map(lastName) });

/** The most legs a parlay can have (the backend's limit for /api/sim/parlay). */
export const MAX_PARLAY_LEGS = 8;
export const PARLAY_FULL_MESSAGE = `Parlays can have a max of ${MAX_PARLAY_LEGS} legs`;
